import HorizontalDatePicker from '@/components/HorizontalDatePicker';
import PushSheet from '@/components/PushSheet';
import { useOrders, type OrderFilters } from '@/features/orders/hooks/useOrders';
import { labelChipStyle } from '@/features/labels/utils/label-color';
import { orderStatusColor } from '@/features/orders/utils/order-status';
import { paymentMethodIcon, paymentMethodLabel } from '@/features/orders/utils/payment-method';
import { paymentPill } from '@/features/orders/utils/payment-pill';
import { useT } from '@/i18n';
import type { Translations } from '@/i18n/locales/en';
import { formatMoney } from '@/utils/money';
import { useMediaQuery } from '@/utils/useMediaQuery';
import type { OnlineOrder, OrderStatus, OrderType } from '@restaurant/api-client';
import { Link as F7Link, Page, Popover, f7 } from 'framework7-react';
import type { Router } from 'framework7/types';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * Order History - deliberately *off-pattern* from the rest of the dashboard.
 * iOS-native feel: a large title and a compact filter bar that opens a bottom
 * **modal sheet**. The sheet holds a month navigator (prev / current / next +
 * calendar arrows), an inline month calendar (tap start day, tap end day) and
 * status / type filters. The owner edits a draft and taps Apply; the list below
 * filters to that range. No revenue/summary chrome: just the orders.
 */

type StatusFilter = OrderStatus | 'ALL';
type TypeFilter = OrderType | 'ALL';

interface FilterValue {
  status: StatusFilter;
  type: TypeFilter;
  date: Date;
}

// Status/type filter tab labels resolved from the current locale (the segmented
// controls and the active-chip summary key off the stable `key`, not the label).
type HistoryT = Translations['orders']['history'];
const statusTabs = (h: HistoryT): { key: StatusFilter; label: string }[] => [
  { key: 'ALL', label: h.tabs.all },
  { key: 'PENDING', label: h.tabs.pending },
  { key: 'CONFIRMED', label: h.tabs.active },
  { key: 'COMPLETED', label: h.tabs.done },
  { key: 'CANCELLED', label: h.tabs.void },
];
const typeTabs = (t: Translations): { key: TypeFilter; label: string }[] => [
  { key: 'ALL', label: t.orders.history.tabs.all },
  { key: 'PICKUP', label: t.orders.type.PICKUP },
  { key: 'DELIVERY', label: t.orders.type.DELIVERY },
];

// ---- date helpers (local timezone) -----------------------------------------
const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
// The selected calendar cell as a plain YYYY-MM-DD, read from the date's LOCAL
// Y/M/D (the numbers the user tapped) - never toISOString(), which would shift
// the day across the UTC boundary. The server resolves this into the
// restaurant-timezone day bounds, so the filter matches the Orders board exactly.
const dayParam = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
// Inverse of dayParam: a YYYY-MM-DD (from the horizontal date strip) back into a
// LOCAL-midnight Date, so the strip and the query stay in the same day the user
// tapped (no UTC shift).
const parseLocalDay = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const dayKey = (d: Date): number => startOfDay(d).getTime();
const sameDay = (a: Date, b: Date): boolean => dayKey(a) === dayKey(b);

function dateLabel(d: Date, rel: { today: string; yesterday: string }): string {
  const today = startOfDay(new Date());
  if (sameDay(d, today)) return rel.today;
  const yest = new Date(today);
  yest.setDate(yest.getDate() - 1);
  if (sameDay(d, yest)) return rel.yesterday;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function defaultFilter(): FilterValue {
  return { status: 'ALL', type: 'ALL', date: startOfDay(new Date()) };
}

const clockTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

function OrderHistoryPage({ f7router }: { f7router: Router.Router }) {
  const t = useT();
  const h = t.orders.history;
  const [applied, setApplied] = useState<FilterValue>(defaultFilter);
  const [filterOpen, setFilterOpen] = useState(false);

  const filters = useMemo<OrderFilters>(() => {
    const f: OrderFilters = { limit: 100 };
    if (applied.status !== 'ALL') f.status = applied.status;
    if (applied.type !== 'ALL') f.type = applied.type;
    // Scope by the restaurant-local day + order SERVICE instant (same server path
    // the Orders board uses), NOT createdAt in the browser tz - so History and the
    // board agree on which orders belong to a given day.
    f.day = dayParam(applied.date);
    return f;
  }, [applied]);

  const { data, isLoading, isError, refetch, isFetching } = useOrders(filters);
  const orders = useMemo(() => data?.items ?? [], [data]);

  // The list is scoped to exactly one selected day server-side, so every order
  // belongs under that day's header. Group them all under the selected-day label
  // (not each order's createdAt - a today-scheduled order created yesterday would
  // otherwise land under a stray "Yesterday" heading that contradicts the filter).
  const groups = useMemo<[string, OnlineOrder[]][]>(() => {
    if (orders.length === 0) return [];
    const sorted = [...orders].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return [[dateLabel(applied.date, h), sorted]];
  }, [orders, applied.date, h]);

  const activeChips: string[] = [];
  if (applied.status !== 'ALL')
    activeChips.push(statusTabs(h).find((s) => s.key === applied.status)?.label ?? '');
  if (applied.type !== 'ALL')
    activeChips.push(typeTabs(t).find((tp) => tp.key === applied.type)?.label ?? '');

  const openOrder = (o: OnlineOrder) => f7router.navigate(`/orders/${o.id}`);

  return (
    <Page name="order-history" style={{ background: 'var(--f7-page-bg-color, #efeff4)' }}>
      <style>{historyCss}</style>

      <div className="oh-scroll">
        <header className="oh-hero">
          <div className="oh-hero-row">
            <button type="button" className="oh-back" onClick={() => f7router.back()}>
              <i className="f7-icons">chevron_left</i>
              {t.orders.page.title}
            </button>
            <button
              type="button"
              className="oh-refresh"
              onClick={() => refetch()}
              aria-label={h.refresh}
              aria-busy={isFetching}
              disabled={isFetching}
            >
              <i className={`f7-icons${isFetching ? ' oh-spin' : ''}`}>arrow_clockwise</i>
            </button>
          </div>
          <h1 className="oh-title">{h.title}</h1>
        </header>

        {/* Day scrubber + filter merged into ONE bar: the same horizontal date
            strip as the reservations board, with the status/type filter button
            pinned at its trailing edge (next to the calendar). Picking a day
            applies immediately; the filter button opens the sheet. */}
        <div className="oh-datestrip">
          <HorizontalDatePicker
            selectedDate={dayParam(applied.date)}
            onDateSelect={(s) => setApplied((a) => ({ ...a, date: parseLocalDay(s) }))}
            trailing={
              <button
                type="button"
                className="oh-strip-filter"
                onClick={() => setFilterOpen(true)}
                aria-label={h.filters}
              >
                <i className="f7-icons">line_horizontal_3_decrease</i>
                {activeChips.length > 0 && <span className="oh-strip-filter-dot" />}
              </button>
            }
          />
        </div>

        <div className="oh-body">
          {isLoading && (
            <div className="oh-empty">
              <i className="f7-icons oh-spin oh-empty-icon">arrow_2_circlepath</i>
              <span>{h.loading}</span>
            </div>
          )}

          {isError && !isLoading && (
            <div className="oh-empty">
              <i className="f7-icons oh-empty-icon" style={{ color: '#ff3b30' }}>
                exclamationmark_triangle
              </i>
              <span>{h.loadError}</span>
              <button type="button" className="oh-retry" onClick={() => refetch()}>
                {h.tryAgain}
              </button>
            </div>
          )}

          {!isLoading && !isError && orders.length === 0 && (
            <div className="oh-empty">
              <i className="f7-icons oh-empty-icon">tray</i>
              <span>{h.empty}</span>
            </div>
          )}

          {groups.map(([day, dayOrders]) => (
            <section key={day} className="oh-group">
              <h2 className="oh-group-title">{day}</h2>
              <div className="oh-card-stack">
                {dayOrders.map((o) => {
                  const itemCount = o.items.reduce((n, i) => n + i.quantity, 0);
                  const accent = orderStatusColor[o.status];
                  const labels = o.labels ?? [];
                  const pill = paymentPill(o, t.orders.pill);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      className="oh-card"
                      onClick={() => openOrder(o)}
                    >
                      <span className="oh-card-rail" style={{ background: accent }} />
                      <div className="oh-card-main">
                        <div className="oh-card-top">
                          <span className="oh-card-name">{o.customerName}</span>
                          {/* Triage labels ride the name line rather than taking
                              one of their own: on a wide card that line is mostly
                              empty between the name and the total, and a row that
                              grows a whole line only for tagged orders makes the
                              list jump. Capped, and the NAME truncates first — a
                              cut name still identifies the order, a vanished
                              label doesn't. */}
                          {labels.length > 0 && (
                            <span className="oh-labels">
                              {/* Chips clip, the counter never does — it lives
                                  OUTSIDE the clipped box (see the CSS note). */}
                              <span className="oh-label-chips">
                                {labels.slice(0, 2).map((label) => (
                                  <span
                                    key={label.id}
                                    className="oh-label"
                                    style={labelChipStyle(label.color, true)}
                                  >
                                    {label.name}
                                  </span>
                                ))}
                              </span>
                              {labels.length > 2 && (
                                <span className="oh-label-more">
                                  {t.labels.more(labels.length - 2)}
                                </span>
                              )}
                            </span>
                          )}
                          <span className="oh-card-total">
                            {formatMoney(o.totalCents, o.currency)}
                          </span>
                        </div>
                        <div className="oh-card-meta">
                          <span className="oh-chip" style={{ color: accent, borderColor: accent }}>
                            <span className="oh-dot" style={{ background: accent }} />
                            {h.statusLabel[o.status]}
                          </span>
                          {/* How it was paid, and whether the money actually
                              landed. History is a ledger — unlike the live queue,
                              the rail is worth showing on every row, and the
                              gateway's brand names WHICH account to reconcile
                              against. The status pill still follows the shared
                              rule: nothing for the expected states (awaiting
                              in-person collection, cancelled-void). */}
                          {o.paymentMethodKind && (
                            <span className="oh-meta-item">
                              <i className="f7-icons oh-meta-icon">
                                {paymentMethodIcon(o.paymentMethodKind)}
                              </i>
                              {paymentMethodLabel(
                                o.paymentMethodKind,
                                t.orders.methodKind,
                                o.paymentMethodId,
                              )}
                              {pill && (
                                <span
                                  className="oh-pay-pill"
                                  style={{ background: pill.bg, color: pill.color }}
                                >
                                  {pill.label}
                                </span>
                              )}
                            </span>
                          )}
                          <span className="oh-meta-item">
                            <i className="f7-icons oh-meta-icon">
                              {o.type === 'PICKUP' ? 'bag' : 'car_fill'}
                            </i>
                            {o.type === 'PICKUP' ? t.orders.type.PICKUP : t.orders.type.DELIVERY}
                          </span>
                          <span className="oh-meta-item">{t.orders.itemCount(itemCount)}</span>
                          <span className="oh-meta-item oh-meta-time">
                            {clockTime(o.createdAt)}
                          </span>
                        </div>
                      </div>
                      <i className="f7-icons oh-card-chevron">chevron_right</i>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

          <div className="oh-footer-pad" />
        </div>
      </div>

      <OrderHistoryFilterSheet
        open={filterOpen}
        value={applied}
        onApply={(v) => {
          setApplied(v);
          setFilterOpen(false);
        }}
        onClose={() => setFilterOpen(false)}
      />
    </Page>
  );
}

/**
 * The filter modal sheet. Holds its own *draft* state seeded from the applied
 * value each time it opens, so editing never re-queries behind the open sheet -
 * the page only refilters when the owner taps Apply.
 */
function OrderHistoryFilterSheet({
  open,
  value,
  onApply,
  onClose,
}: {
  open: boolean;
  value: FilterValue;
  onApply: (v: FilterValue) => void;
  onClose: () => void;
}) {
  const t = useT();
  const h = t.orders.history;
  // Use the popover from 768px up - the same width where F7 retargets a bottom
  // sheet into a centred floating card (styles.css). Below it, the bottom sheet.
  const isWide = useMediaQuery('(min-width: 768px)');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popoverRef = useRef<any>(null);
  const [status, setStatus] = useState<StatusFilter>(value.status);
  const [type, setType] = useState<TypeFilter>(value.type);

  // Re-seed the draft from the applied value every time the sheet opens. The day
  // itself is owned by the horizontal strip on the page, not this sheet.
  useEffect(() => {
    if (!open) return;
    setStatus(value.status);
    setType(value.type);
    // Snapshot on open only - intentionally not reacting to `value` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Desktop: drive the popover imperatively. Anchor it to the compact filter
  // icon at the RIGHT of the bar (not the full-width button, whose centre would
  // drop the popover into the middle of the bar) - F7 clamps it into view, so it
  // reads as a right-aligned dropdown from the filter control. Mobile uses the
  // PushSheet.
  useEffect(() => {
    if (!isWide) return;
    const el = popoverRef.current?.el;
    if (!el) return;
    if (open) f7.popover.open(el, '.oh-strip-filter');
    else f7.popover.close(el);
  }, [open, isWide]);

  const reset = () => {
    const d = defaultFilter();
    setStatus(d.status);
    setType(d.type);
  };

  // The filter card (header + calendar + segments + apply), shared by the desktop
  // popover and the mobile sheet. The header is a plain in-flow element inside the
  // card - NOT an F7 <Toolbar> - so it can never overlap the content below it.
  const sheetCard = (header: ReactNode) => (
    <div className="oh-sheet">
      {header}
      <div className="oh-sheet-body">
        {/* Status segmented */}
        <div className="oh-segment">
          {statusTabs(h).map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`oh-seg-btn${status === tab.key ? ' is-active' : ''}`}
              onClick={() => setStatus(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Type segmented */}
        <div className="oh-segment oh-segment-type">
          {typeTabs(t).map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`oh-seg-btn${type === tab.key ? ' is-active' : ''}`}
              onClick={() => setType(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="oh-sheet-foot">
        <button
          type="button"
          className="oh-apply"
          onClick={() => onApply({ status, type, date: value.date })}
        >
          {h.showOrders}
        </button>
      </div>
    </div>
  );

  // Desktop: a popover anchored to the filter button. Always mounted; the effect
  // above opens/closes it. The backdrop tap fires `onPopoverClosed` → onClose.
  if (isWide) {
    return (
      <Popover ref={popoverRef} className="oh-filter-popover" onPopoverClosed={onClose}>
        {sheetCard(
          <div className="oh-sheet-head">
            <span className="oh-sheet-title">{h.filters}</span>
            <F7Link className="oh-tb-reset" onClick={reset}>
              {h.reset}
            </F7Link>
          </div>,
        )}
      </Popover>
    );
  }

  // Mobile: the bottom modal sheet. Cap the height so the sheet's top always
  // stays clear of the notch. The header lives inside the card (in-flow), so it
  // can't overlap the content.
  return (
    <PushSheet
      open={open}
      onClose={onClose}
      style={{ height: 'auto', maxHeight: 'calc(100dvh - env(safe-area-inset-top) - 16px)' }}
    >
      {sheetCard(
        <div className="oh-sheet-head">
          <F7Link className="oh-tb-cancel" onClick={onClose}>
            {t.cancel}
          </F7Link>
          <span className="oh-sheet-title">{h.filters}</span>
          <F7Link className="oh-tb-reset" onClick={reset}>
            {h.reset}
          </F7Link>
        </div>,
      )}
    </PushSheet>
  );
}

const historyCss = `
.page[data-name='order-history'] {
  /* Match the Orders tab canvas exactly: read F7's own page background so the
     tone (and dark mode) stays identical to every other page. */
  --oh-bg: var(--f7-page-bg-color, #efeff4);
}
.oh-scroll {
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: var(--oh-bg);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
}
.oh-hero {
  padding: max(env(safe-area-inset-top), 12px) 20px 14px;
  /* No background of its own - sits on the page canvas like .oh-body. */
  background: transparent;
}
.oh-hero-row { display: flex; align-items: center; justify-content: space-between; height: 36px; }
.oh-back {
  display: inline-flex; align-items: center; gap: 1px;
  background: none; border: none; padding: 0; margin: 0;
  color: #007aff; font-size: 17px; font-weight: 400; cursor: pointer;
}
.oh-back i { font-size: 26px; margin-left: -6px; }
.oh-refresh {
  width: 34px; height: 34px; border-radius: 50%; border: none;
  background: rgba(120,120,128,0.12); color: #007aff;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.oh-refresh i { font-size: 18px; }
.oh-refresh:disabled { opacity: 0.5; cursor: default; }
.oh-title { margin: 8px 0 0; font-size: 34px; font-weight: 800; letter-spacing: -0.5px; color: #1c1c1e; }

/* Compact filter bar */
/* The horizontal date strip is now the sole top control (the day picker AND the
   filter button live in it), so it's the sticky bar - frosted blur keeps it
   legible when order cards scroll underneath. */
.oh-datestrip {
  position: sticky; top: 0; z-index: 10;
  padding: 8px 16px;
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
}
/* Filter button merged into the strip's trailing edge, matching the calendar
   button next to it (.rsv-date-strip-cal-btn). */
.oh-strip-filter {
  position: relative;
  flex: 0 0 auto;
  width: 40px; height: 40px; margin-right: 6px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(60, 60, 67, 0.12); border-radius: 10px;
  background: transparent; color: var(--f7-theme-color); cursor: pointer;
}
.oh-strip-filter .f7-icons { font-size: 20px; }
/* Dot badge when any status/type filter is active. */
.oh-strip-filter-dot {
  position: absolute; top: 6px; right: 6px;
  width: 8px; height: 8px; border-radius: 50%;
  background: #ff3b30;
}

/* Sheet */
.oh-sheet {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  background: #f2f2f7;
  border-radius: 16px 16px 0 0;
  display: flex; flex-direction: column;
}
/* Header lives inside the card as a plain in-flow row (not an F7 toolbar), so it
 * stacks above the content and can never overlap it. */
.oh-sheet-head {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 16px 16px 6px;
}
.oh-sheet-title { font-weight: 700; color: #1c1c1e; font-size: 17px; }
.oh-sheet-head .oh-tb-cancel { color: #8e8e93; }
.oh-sheet-head .oh-tb-reset { color: #007aff; font-weight: 600; }
.oh-sheet-body { padding: 6px 16px 8px; max-height: 64vh; overflow-y: auto; -webkit-overflow-scrolling: touch; }
.oh-sheet-foot {
  /* 14px breathing room ABOVE the home-indicator inset, not max()'d into it. */
  padding: 10px 16px calc(14px + env(safe-area-inset-bottom));
  border-top: 0.5px solid rgba(60,60,67,0.12); background: #f2f2f7;
}
.oh-apply {
  width: 100%; border: none; cursor: pointer; padding: 14px; border-radius: 14px;
  background: #007aff; color: #fff; font-size: 17px; font-weight: 700;
}

/* Desktop: the same filter form in a popover anchored to the filter button. */
.oh-filter-popover { width: 360px; }
.oh-filter-popover .popover-inner { border-radius: 14px; overflow: hidden; }
.oh-filter-popover .oh-sheet { border-radius: 0; }
.oh-filter-popover .oh-sheet-body { max-height: min(64vh, 460px); }
/* No home indicator under a floating popover → drop the safe-area inset. */
.oh-filter-popover .oh-sheet-foot { padding-bottom: 14px; }
.oh-apply:active { background: #0067db; }

.oh-range-display {
  text-align: center; font-size: 15px; font-weight: 700; color: #007aff;
}

/* Inline calendar */
.oh-cal-card {
  margin-top: 10px; padding: 12px 10px 8px; border-radius: 16px;
  background: #ffffff; box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}
.oh-cal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.oh-cal-title { flex: 1; text-align: center; font-size: 15px; font-weight: 700; color: #1c1c1e; }
.oh-cal-nav {
  width: 30px; height: 30px; flex-shrink: 0; border: none; cursor: pointer;
  border-radius: 50%; background: rgba(0,122,255,0.10); color: #007aff;
  display: inline-flex; align-items: center; justify-content: center;
}
.oh-cal-nav:active { background: rgba(0,122,255,0.22); }
.oh-cal-nav i { font-size: 18px; }
.oh-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.oh-cal-wd { text-align: center; font-size: 11px; font-weight: 600; color: #8e8e93; padding: 2px 0 6px; }
.oh-cal-empty { aspect-ratio: 1; }
.oh-cal-day {
  aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
  border: none; background: transparent; cursor: pointer;
  font-size: 14px; font-weight: 500; color: #1c1c1e; border-radius: 50%; padding: 0;
}
.oh-cal-day.is-today { color: #007aff; font-weight: 700; }
.oh-cal-day.is-future { color: #c7c7cc; }
.oh-cal-day:disabled { cursor: default; }
.oh-cal-nav:disabled { opacity: 0.35; cursor: default; }
.oh-cal-day.is-edge { background: #007aff; color: #fff; font-weight: 700; border-radius: 50%; }

/* Status segmented */
.oh-segment {
  display: flex; background: rgba(120,120,128,0.12);
  border-radius: 10px; padding: 2px; gap: 2px; margin-top: 14px;
}
.oh-seg-btn {
  flex: 1; border: none; background: transparent; cursor: pointer;
  padding: 7px 4px; border-radius: 8px; font-size: 13px; font-weight: 600; color: #3c3c43;
  transition: background 0.18s ease, box-shadow 0.18s ease;
}
.oh-seg-btn.is-active {
  background: #ffffff; color: #1c1c1e;
  box-shadow: 0 1px 4px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.08);
}

/* Type segmented */
.oh-segment-type { margin-top: 10px; }

.oh-body { padding: 8px 16px 0; }
.oh-group { margin-top: 14px; }
.oh-group-title {
  margin: 0 4px 8px; font-size: 13px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.6px; color: #8e8e93;
}
.oh-card-stack { display: flex; flex-direction: column; gap: 10px; }
.oh-card {
  position: relative; display: flex; align-items: center; gap: 12px;
  width: 100%; text-align: left; cursor: pointer;
  background: #ffffff; border: none; border-radius: 16px;
  padding: 14px 14px 14px 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 1px rgba(0,0,0,0.04);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.oh-card:active { transform: scale(0.98); box-shadow: 0 1px 2px rgba(0,0,0,0.08); }
.oh-card-rail {
  position: absolute; left: 0; top: 12px; bottom: 12px; width: 4px; border-radius: 0 4px 4px 0;
}
.oh-card-main { flex: 1; min-width: 0; }
.oh-card-top { display: flex; align-items: center; gap: 8px; }
.oh-card-name {
  font-size: 16px; font-weight: 600; color: #1c1c1e;
  min-width: 0; flex: 0 1 auto;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* margin-left:auto, not space-between: the name line now carries the label chips
   between the two, and only the total may be pinned to the right edge. */
.oh-card-total {
  font-size: 16px; font-weight: 700; color: #1c1c1e;
  flex: 0 0 auto; margin-left: auto; padding-left: 8px;
}
.oh-card-meta {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; margin-top: 7px;
}
/* Owner-defined triage labels. Colors come from the label row itself (inline),
   with the text color derived from luminance - see orders/utils/label-color.ts.
   Sits on the name line: nowrap + overflow keeps the row one line tall however
   many tags an order carries. Two chips + a +N counter, not three: sharing the
   line with the name means the third chip cost more than it told anyone. */
.oh-labels {
  display: flex; align-items: center; gap: 4px;
  flex: 0 1 auto; min-width: 0; max-width: 46%;
  flex-wrap: nowrap;
}
/* Only the CHIPS clip. The +N counter is a sibling of this box, not a child, so
   two long labels can never push it out of sight - "there are more" has to
   survive exactly the case that creates it. */
.oh-label-chips {
  display: flex; align-items: center; gap: 4px;
  flex: 0 1 auto; min-width: 0; flex-wrap: nowrap; overflow: hidden;
}
.oh-label {
  flex: 0 1 auto; min-width: 30px; max-width: 120px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-size: 10px; font-weight: 600; line-height: 1.5;
  padding: 1px 8px; border-radius: 999px; border: 1px solid;
}
.oh-label-more { flex: 0 0 auto; font-size: 10px; color: #8e8e93; font-weight: 600; }
.oh-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 700; padding: 3px 8px;
  border: 1px solid; border-radius: 999px; background: rgba(255,255,255,0.6);
}
.oh-dot { width: 6px; height: 6px; border-radius: 50%; }
.oh-meta-item {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 12px; color: #8e8e93; font-weight: 500;
}
.oh-meta-icon { font-size: 13px; }
/* Payment state, inside the payment meta item so the rail and its outcome read
   as one fact ("Menucat · Paid") instead of two unrelated chips. Muted-weight
   square-ish pill, deliberately quieter than the round status chip that leads
   the line - the order's own status is what the eye should land on first. */
.oh-pay-pill {
  margin-left: 5px; font-size: 11px; font-weight: 700;
  padding: 1px 6px; border-radius: 6px;
}
.oh-meta-time { margin-left: auto; }
.oh-card-chevron { font-size: 17px; color: #c7c7cc; flex-shrink: 0; }

.oh-empty {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 64px 24px; color: #8e8e93; text-align: center; font-size: 15px;
}
.oh-empty-icon { font-size: 40px; color: #c7c7cc; }
.oh-retry {
  margin-top: 4px; border: none; background: #007aff; color: #fff;
  padding: 8px 18px; border-radius: 999px; font-size: 14px; font-weight: 600; cursor: pointer;
}
.oh-footer-pad { height: max(env(safe-area-inset-bottom), 24px); }
.oh-spin { animation: oh-spin 0.9s linear infinite; }
@keyframes oh-spin { to { transform: rotate(360deg); } }
`;

export default OrderHistoryPage;
